import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import { Spinner } from '@memoresa/ui-components';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useQueries } from 'react-query';
import { Slide } from 'react-slideshow-image';
import 'react-slideshow-image/dist/styles.css';
import { ImagesService } from '../../services/images/ImagesService';
import useWindowSize from '../../utils/hooks/useWindowSize';
import { ImagePreviewDialog } from '../dialogs/productDetailsPage/ImagesPreviewDialog';

export const Arrow = props => {
  return (
    <div
      {...props}
      onClick={e => {
        e.stopPropagation();
        props.onClick(e);
      }}
      className={clsx(
        'relative cursor-pointer w-[29px] h-[29px] select-none',
        props.variant === 'dialog' ? 'text-white' : 'text-primary'
      )}
    >
      {props.side === 'left' && (
        <Icon
          path={mdiChevronLeft}
          size={2.5}
          className="absolute bottom-0 left-0 -translate-x-1/4 translate-y-1/4"
        />
      )}
      {props.side === 'right' && (
        <Icon
          path={mdiChevronRight}
          size={2.5}
          className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4"
        />
      )}
    </div>
  );
};
export const ImageSlider = ({
  images,
  setMainImage,
}: {
  images: string[];
  setMainImage: React.Dispatch<string>;
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const { isSm, isMd } = useWindowSize();
  const queries: any[] = [];
  images.forEach(image =>
    queries.push({
      queryKey: ['imagesSlider', image],
      queryFn: () => ImagesService.getProductImage({ id: image }),
      staleTime: Infinity,
    })
  );
  const results = useQueries(queries);
  useEffect(() => {
    if (!results[0].isLoading && results[0].data) {
      setMainImage(results[0].data as string);
    }
  }, [results]); //eslint-disable-line
  const { asPath } = useRouter();

  useEffect(() => {
    setCurrentSlide(0);
  }, [asPath]);

  return (
    <div
      className="bg-[#C2E8D7] w-screen sm:w-full pt-4 pb-4 px-4 h-full flex flex-col sm:space-y-4"
      key={images[0]}
    >
      {!isSm ? (
        <Slide
          easing="ease-out"
          canSwipe={!isSm}
          autoplay={false}
          transitionDuration={300}
          onChange={(from, to) => setCurrentSlide(to)}
          indicators={index =>
            !isSm ? (
              <div
                className={clsx(
                  'rounded-full  w-2 h-2',
                  currentSlide === index ? 'bg-primary' : 'bg-[#03335480]'
                )}
              />
            ) : (
              <div />
            )
          }
          prevArrow={<Arrow side="left" />}
          nextArrow={<Arrow side="right" />}
          arrows={!isSm}
        >
          {results.map((result, i) => {
            if (result.isLoading)
              return (
                <div
                  key={i}
                  className="flex items-center justify-center w-full h-full"
                >
                  <Spinner size={48} />
                </div>
              );
            return (
              <div
                className="flex items-center justify-center w-full h-full each-slide-effect"
                key={i}
              >
                <div
                  className={clsx(
                    'h-full flex items-center justify-center w-full px-10'
                  )}
                >
                  <img
                    src={`data:image/png;base64,${result.data}`}
                    alt=""
                    className="object-contain w-full h-full sm:w-1/2 sm:h-1/2 cursor-zoom-in"
                    onClick={() => setIsPreviewDialogOpen(true)}
                  />
                </div>
              </div>
            );
          })}
        </Slide>
      ) : (
        <div className="flex items-center justify-center w-full pt-5 each-slide-effect">
          <div
            className={clsx('h-full flex items-center justify-center w-full ')}
          >
            {results?.[currentSlide]?.isLoading && (
              <div className="flex items-center justify-center w-full h-full">
                <Spinner size={48} />
              </div>
            )}
            {results?.[currentSlide]?.isSuccess && (
              <img
                src={`data:image/png;base64,${results[currentSlide].data}`}
                alt=""
                className="object-contain w-full h-full cursor-zoom-in"
                onClick={() => setIsPreviewDialogOpen(true)}
              />
            )}
          </div>
        </div>
      )}
      {isSm && (
        <Slide
          easing="ease-out"
          canSwipe={isTablet || isMobile}
          slidesToShow={results.length < 4 ? results.length : isMd ? 3 : 2}
          autoplay={false}
          transitionDuration={300}
          // onChange={(from, to) => setCurrentSlide(to)}
          prevArrow={<Arrow side="left" />}
          nextArrow={<Arrow side="right" />}
        >
          {results.map((result, i) => {
            return (
              <div
                className="flex items-center justify-center w-full h-full sm:h-auto each-slide-effect"
                key={i}
              >
                <div className="flex items-center justify-center w-full h-full px-4">
                  {results[0].isSuccess && (
                    <img
                      src={`data:image/png;base64,${result.data}`}
                      alt=""
                      className={clsx(
                        'object-contain w-full h-full cursor pointer opacity-0 transition-opacity duration-200'
                      )}
                      onClick={() => {
                        setCurrentSlide(i);
                      }}
                      onLoad={e => (e.currentTarget.style.opacity = '1')}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </Slide>
      )}
      {isPreviewDialogOpen && (
        <ImagePreviewDialog
          open={isPreviewDialogOpen}
          onClose={() => setIsPreviewDialogOpen(false)}
          images={results as any}
          startingIndex={currentSlide}
        />
      )}
    </div>
  );
};
